
import { useEffect, useRef, useState } from 'react';

interface FaqItem {
  question: string;
  answer: string;
}

const faqs: FaqItem[] = [
  {
    question: '¿Cómo eliges los productos?',
    answer:
      'Investigo, comparo y pruebo antes de recomendar nada. Si algo no me convence, simplemente no aparece aquí. No acepto productos a cambio de reseñas positivas. Solo recomiendo lo que usaría yo misma.',
  },
  {
    question: '¿Ganas dinero con esto?',
    answer:
      'Sí, con links de afiliado. Transparencia total. Cuando compras a través de mis enlaces, recibo una pequeña comisión sin coste extra para ti. Pero eso nunca influye en lo que recomiendo: si algo no me gusta, no lo verás aquí.',
  },
  {
    question: '¿Con qué frecuencia publicas contenido nuevo?',
    answer:
      'Publico cuando encuentro algo que realmente merece la pena. No tengo un calendario fijo porque prefiero calidad a cantidad. Si te suscribes a la newsletter, te aviso cuando hay algo nuevo.',
  },
];

export default function FaqSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!('IntersectionObserver' in window)) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setInView(true);
        }
      },
      { threshold: 0.2 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section
      ref={sectionRef}
      className="relative bg-white py-16 lg:py-20 px-8 md:px-12 lg:px-[8%] overflow-hidden"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          }),
        }}
      />

      <div className="relative z-10 max-w-3xl mx-auto">
        <div
          className={`text-center mb-10 transition-all duration-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-[clamp(1.8rem,3.5vw,2.6rem)] font-bold text-[#222222] leading-tight tracking-[-0.01em]">
            Preguntas frecuentes 🤔
          </h2>
        </div>

        <div className="flex flex-col gap-3">
          {faqs.map((faq, index) => (
            <div
              key={faq.question}
              className={`border border-[#E8DED2] rounded-lg overflow-hidden transition-all duration-700 ${
                inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${200 + index * 120}ms` }}
            >
              <button
                onClick={() => toggleFaq(index)}
                className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-[#FAF9F7] transition-colors duration-200"
                aria-expanded={openIndex === index}
              >
                <h4 className="text-[15px] font-medium text-[#222222] pr-4">
                  <a href={`#faq-${index}`} className="no-underline text-inherit">
                    {faq.question}
                  </a>
                </h4>
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i
                    className={`ri-arrow-down-s-line text-[20px] text-[#A39B92] transition-transform duration-300 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  ></i>
                </div>
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-[300px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 pb-5">
                  <p className="text-[14px] text-[#6B6860] leading-[1.8] font-light">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
